import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex justify-center items-center h-32"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "mb-6" }
const _hoisted_4 = { class: "text-lg font-medium text-gray-900 mb-2" }
const _hoisted_5 = { class: "text-sm text-gray-600" }
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" }
const _hoisted_7 = {
  key: 0,
  class: "flex items-center justify-center h-full text-sm text-gray-400 italic"
}
const _hoisted_8 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_9 = ["onDragstart"]
const _hoisted_10 = { class: "flex-1 min-w-0" }
const _hoisted_11 = { class: "text-sm font-medium text-gray-900 block truncate" }
const _hoisted_12 = ["onClick", "disabled"]
const _hoisted_13 = {
  key: 0,
  class: "flex items-center justify-center h-full text-sm text-gray-400 italic"
}
const _hoisted_14 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_15 = ["onDragstart"]
const _hoisted_16 = ["onClick", "disabled"]
const _hoisted_17 = { class: "flex-1 min-w-0" }
const _hoisted_18 = { class: "text-sm font-medium text-gray-900 block truncate" }

import { computed, ref, watch, onMounted } from 'vue';
import { usePermissionStore } from '@/store/permissions';
import { useNotificationStore } from '@/store/notifications';
import Modal from './modal.vue';

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: { String: string };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AssignPermissionModal',
  props: {
    show: { type: Boolean },
    user: {}
  },
  emits: ["close", "permissionsUpdated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const permissionStore = usePermissionStore();
const notificationStore = useNotificationStore();

const draggingPermission = ref<Permission | null>(null);
const draggingFrom = ref<'available' | 'assigned' | null>(null);
const isDraggingOver = ref<'available' | 'assigned' | null>(null);

const availablePermissions = computed(() =>
  permissionStore.permissions.filter(
    (perm) => !permissionStore.userPermissions.some((userPerm) => userPerm.id === perm.id)
  )
);

function handleDragStart(permission: Permission, from: 'available' | 'assigned', event: DragEvent) {
  draggingPermission.value = permission;
  draggingFrom.value = from;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', permission.id);
  }
}

function handleDragEnd() {
  draggingPermission.value = null;
  draggingFrom.value = null;
  isDraggingOver.value = null;
}

function handleDragOver(target: 'available' | 'assigned') {
  if (draggingFrom.value && draggingFrom.value !== target) {
    isDraggingOver.value = target;
  }
}

function handleDragLeave() {
  isDraggingOver.value = null;
}

async function assignPermission(permissionId: string) {
  try {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.assignPermission(props.user.id, permissionId);
    } else if (purpose === 'local') {
      await permissionStore.assignAdminPermission(props.user.id, permissionId);
    }
    if (permissionStore.error) {
      notificationStore.showError('Permission Assignment Failed', permissionStore.error);
    } else {
      notificationStore.showSuccess('Permission Assigned', 'Permission has been assigned successfully.');
      emit('permissionsUpdated');
    }
  } catch (err) {
    notificationStore.showError('Assignment Error', 'An unexpected error occurred while assigning permission.');
  }
}

async function removePermission(permissionId: string) {
  try {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.removePermission(props.user.id, permissionId);
    } else if (purpose === 'local') {
      await permissionStore.removeAdminPermission(props.user.id, permissionId);
    }
    if (permissionStore.error) {
      notificationStore.showError('Permission Removal Failed', permissionStore.error);
    } else {
      notificationStore.showSuccess('Permission Removed', 'Permission has been removed successfully.');
      emit('permissionsUpdated');
    }
  } catch (err) {
    notificationStore.showError('Removal Error', 'An unexpected error occurred while removing permission.');
  }
}

async function handleDrop(target: 'available' | 'assigned', event: DragEvent) {
  event.preventDefault();
  isDraggingOver.value = null;
  const purpose = localStorage.getItem('purpose');

  if (!draggingPermission.value || draggingFrom.value === target) return;

  if (target === 'assigned') {
    await assignPermission(draggingPermission.value.id);
  } else {
    await removePermission(draggingPermission.value.id);
  }

  handleDragEnd();
}

watch(() => props.show, async (newShow) => {
  if (newShow && props.user) {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.fetchPermissions();
      await permissionStore.fetchUserPermissions(props.user.id);
    } else if (purpose === 'local') {
      await permissionStore.fetchAdminPermissions();
      await permissionStore.fetchAdminUserPermissions(props.user.id);
    }
  }
});

onMounted(async () => {
  if (props.show && props.user) {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.fetchPermissions();
      await permissionStore.fetchUserPermissions(props.user.id);
    } else if (purpose === 'local') {
      await permissionStore.fetchAdminPermissions();
      await permissionStore.fetchAdminUserPermissions(props.user.id);
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: _ctx.show,
    title: "Manage User Permissions",
    size: "max-w-4xl",
    onClose: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('close')))
  }, {
    footer: _withCtx(() => [
      _createElementVNode("button", {
        onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('close'))),
        class: "px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
      }, " Close ")
    ]),
    default: _withCtx(() => [
      (_unref(permissionStore).loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[6] || (_cache[6] = [
            _createElementVNode("div", { class: "animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" }, null, -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("h4", _hoisted_4, "User: " + _toDisplayString(_ctx.user?.firstName) + " " + _toDisplayString(_ctx.user?.lastName), 1),
              _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.user?.emailAddress?.String), 1)
            ]),
            _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-sm text-gray-500 mb-4" }, [
              _createElementVNode("i", { class: "fa-solid fa-info-circle mr-1" }),
              _createTextVNode(" Drag permissions between lists to assign or remove them ")
            ], -1)),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", null, [
                _cache[9] || (_cache[9] = _createElementVNode("h5", { class: "text-md font-medium text-gray-900 mb-3" }, [
                  _createElementVNode("i", { class: "fa-solid fa-list text-gray-500 mr-2" }),
                  _createTextVNode(" Available Permissions ")
                ], -1)),
                _createElementVNode("div", {
                  class: _normalizeClass(["min-h-48 max-h-64 overflow-y-auto border-2 border-dashed border-gray-300 rounded-lg p-3 transition-colors", { 'border-red-400 bg-red-50': isDraggingOver.value === 'available' }]),
                  onDragover: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (handleDragOver('available')), ["prevent"])),
                  onDragleave: handleDragLeave,
                  onDrop: _cache[1] || (_cache[1] = ($event: any) => (handleDrop('available', $event)))
                }, [
                  (availablePermissions.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, " All permissions assigned "))
                    : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availablePermissions.value, (permission) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: permission.id,
                            draggable: "true",
                            onDragstart: ($event: any) => (handleDragStart(permission, 'available', $event)),
                            onDragend: handleDragEnd,
                            class: _normalizeClass(["flex items-center p-3 bg-white border border-gray-200 rounded-md cursor-grab hover:shadow-md hover:border-green-300 transition-all", { 'opacity-50': draggingPermission.value?.id === permission.id }])
                          }, [
                            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "flex-shrink-0 mr-3 text-gray-400" }, [
                              _createElementVNode("i", { class: "fa-solid fa-grip-vertical" })
                            ], -1)),
                            _createElementVNode("div", _hoisted_10, [
                              _createElementVNode("span", _hoisted_11, _toDisplayString(permission.permission), 1)
                            ]),
                            _createElementVNode("button", {
                              onClick: ($event: any) => (assignPermission(permission.id)),
                              disabled: _unref(permissionStore).loading,
                              class: "ml-2 p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors",
                              title: "Assign permission"
                            }, _cache[7] || (_cache[7] = [
                              _createElementVNode("i", { class: "fa-solid fa-chevron-right" }, null, -1)
                            ]), 8, _hoisted_12)
                          ], 42, _hoisted_9))
                        }), 128))
                      ]))
                ], 34)
              ]),
              _createElementVNode("div", null, [
                _cache[12] || (_cache[12] = _createElementVNode("h5", { class: "text-md font-medium text-gray-900 mb-3" }, [
                  _createElementVNode("i", { class: "fa-solid fa-check-circle text-green-500 mr-2" }),
                  _createTextVNode(" Assigned Permissions ")
                ], -1)),
                _createElementVNode("div", {
                  class: _normalizeClass(["min-h-48 max-h-64 overflow-y-auto border-2 border-dashed border-gray-300 rounded-lg p-3 transition-colors", { 'border-green-400 bg-green-50': isDraggingOver.value === 'assigned' }]),
                  onDragover: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (handleDragOver('assigned')), ["prevent"])),
                  onDragleave: handleDragLeave,
                  onDrop: _cache[3] || (_cache[3] = ($event: any) => (handleDrop('assigned', $event)))
                }, [
                  (_unref(permissionStore).userPermissions.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, " Drop permissions here to assign "))
                    : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(permissionStore).userPermissions, (permission) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: permission.id,
                            draggable: "true",
                            onDragstart: ($event: any) => (handleDragStart(permission, 'assigned', $event)),
                            onDragend: handleDragEnd,
                            class: _normalizeClass(["flex items-center p-3 bg-green-50 border border-green-200 rounded-md cursor-grab hover:shadow-md hover:border-green-400 transition-all", { 'opacity-50': draggingPermission.value?.id === permission.id }])
                          }, [
                            _createElementVNode("button", {
                              onClick: ($event: any) => (removePermission(permission.id)),
                              disabled: _unref(permissionStore).loading,
                              class: "mr-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors",
                              title: "Remove permission"
                            }, _cache[10] || (_cache[10] = [
                              _createElementVNode("i", { class: "fa-solid fa-chevron-left" }, null, -1)
                            ]), 8, _hoisted_16),
                            _createElementVNode("div", _hoisted_17, [
                              _createElementVNode("span", _hoisted_18, _toDisplayString(permission.permission), 1)
                            ]),
                            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "flex-shrink-0 ml-3 text-gray-400" }, [
                              _createElementVNode("i", { class: "fa-solid fa-grip-vertical" })
                            ], -1))
                          ], 42, _hoisted_15))
                        }), 128))
                      ]))
                ], 34)
              ])
            ])
          ]))
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})